import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import Icon from '../../../components/AppIcon';

const TransactionChart = ({ data, onDrillDown }) => {
  const [chartType, setChartType] = useState('combined');
  const [timeGranularity, setTimeGranularity] = useState('hourly');

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-elevation-3">
          <p className="text-sm font-semibold text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 mb-1">
              <span className="text-xs text-muted-foreground">{entry?.name}:</span>
              <span className="text-sm font-medium" style={{ color: entry?.color }}>
                {entry?.name === 'Success Rate' 
                  ? `${entry?.value}%` 
                  : entry?.value?.toLocaleString('id-ID')}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="card">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-1">
            Analisis Transaksi Real-time
          </h3>
          <p className="text-sm text-muted-foreground">
            Volume transaksi dan tingkat keberhasilan per periode
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setTimeGranularity('hourly')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-smooth ${
                timeGranularity === 'hourly' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Per Jam
            </button>
            <button
              onClick={() => setTimeGranularity('daily')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-smooth ${
                timeGranularity === 'daily' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Harian
            </button>
            <button
              onClick={() => setTimeGranularity('weekly')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-smooth ${
                timeGranularity === 'weekly' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Mingguan
            </button>
          </div>

          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setChartType('combined')}
              className={`p-1.5 rounded transition-smooth ${
                chartType === 'combined' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
              title="Gabungan"
            >
              <Icon name="BarChart3" size={16} />
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`p-1.5 rounded transition-smooth ${
                chartType === 'bar' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
              title="Bar Chart"
            >
              <Icon name="BarChart2" size={16} />
            </button>
            <button
              onClick={() => setChartType('line')}
              className={`p-1.5 rounded transition-smooth ${
                chartType === 'line' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
              title="Line Chart"
            >
              <Icon name="LineChart" size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="w-full h-80 md:h-96 lg:h-[400px]" aria-label="Transaction Volume and Success Rate Chart">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'combined' ? (
            <ComposedChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="time" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                yAxisId="left"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                label={{ value: 'Volume', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                label={{ value: 'Success Rate (%)', angle: 90, position: 'insideRight', style: { fontSize: '12px' } }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Bar 
                yAxisId="left"
                dataKey="volume" 
                fill="var(--color-primary)" 
                name="Volume Transaksi"
                radius={[4, 4, 0, 0]}
                onClick={(data) => onDrillDown && onDrillDown(data)}
                cursor="pointer"
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="successRate" 
                stroke="var(--color-success)" 
                strokeWidth={2}
                name="Success Rate"
                dot={{ fill: 'var(--color-success)', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          ) : chartType === 'bar' ? (
            <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="time" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar 
                dataKey="volume" 
                fill="var(--color-primary)" 
                name="Volume Transaksi"
                radius={[4, 4, 0, 0]}
                onClick={(data) => onDrillDown && onDrillDown(data)}
                cursor="pointer"
              />
            </BarChart>
          ) : (
            <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="time" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line 
                type="monotone" 
                dataKey="successRate" 
                stroke="var(--color-success)" 
                strokeWidth={2}
                name="Success Rate"
                dot={{ fill: 'var(--color-success)', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TransactionChart;